import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Clock, Shield, X, Mail, Phone, MessageCircle, Eye, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ShariyahCertificationModal = ({
  isOpen,
  onClose,
  shariyahCompleted,
  uploadedFiles = [],
  isViewingFile = false,
  completedAt = null,
  onViewFile,
  onDownloadFile
}) => {
  const handleDownload = (file) => (onDownloadFile || onViewFile)?.(file);
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Shield className="w-6 h-6" />
                  <h2 className="text-xl font-semibold">Shariah Certification</h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-white hover:text-emerald-100 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6 text-[#1a4a1a]">
                {/* Status Section */}
                <div className={`p-4 rounded-lg ${shariyahCompleted ? 'border bg-green-50 border-green-200' : 'border-2 bg-sky-50 border-sky-300'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    {shariyahCompleted ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Clock className="w-5 h-5 text-sky-600" />
                    )}
                    <h3 className={`font-semibold ${shariyahCompleted ? 'text-green-800' : 'text-sky-900'}`}>
                      Status: {shariyahCompleted ? 'Completed' : 'Pending'}
                    </h3>
                  </div>
                  <p className={`text-sm ${shariyahCompleted ? 'text-green-700' : 'text-sky-800'}`}>
                    {shariyahCompleted 
                      ? 'Your Shariah certification has been issued. All documents have been reviewed and approved by our Shariah Advisor.'
                      : 'Your Shariah certification will be issued by our Shariah Advisor. Once it is ready, it will appear below for you to view and download.'
                    }
                  </p>
                </div> 

                {/* Issued Certificates Section */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Your Shariah Certificates</h3>
                  {uploadedFiles.length > 0 ? (
                    <div className="space-y-3">
                      {uploadedFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                              <Shield className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-medium text-sm text-gray-900">{file.name}</p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {file.uploadDate ? new Date(file.uploadDate).toLocaleDateString() : '—'}
                                {file.size ? ` • ${(file.size / 1024 / 1024).toFixed(2)} MB` : ''}
                                <span className="ml-2 text-emerald-600">• Issued by HalalCred</span>
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onViewFile && onViewFile(file)}
                              disabled={isViewingFile}
                              className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isViewingFile ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600 mr-2"></div>
                                  Opening...
                                </>
                              ) : (
                                <>
                                  <Eye className="w-4 h-4 mr-1.5" />
                                  View
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownload(file)}
                              disabled={isViewingFile}
                              className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Download className="w-4 h-4 mr-1.5" />
                              Download
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-sky-200 rounded-lg p-6 text-center bg-sky-50/50">
                      <Clock className="w-10 h-10 text-sky-400 mx-auto mb-3" />
                      <p className="text-sm text-sky-800 font-medium mb-1">No certificates yet</p>
                      <p className="text-sm text-sky-700">
                        Your Shariah certification will appear here once our Shariah Advisor has completed the review and issued it. You will then be able to view and download it.
                      </p>
                    </div>
                  )}
                </div>

                {/* Completed Status */}
                {shariyahCompleted && (
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-green-800">Certification Completed</h4>
                    </div>
                    <p className="text-sm text-green-700 mb-3">
                      Your Shariah certification has been completed on {completedAt ? new Date(completedAt).toLocaleDateString() : 'Not available'}. 
                      All documents have been reviewed and approved by our team.
                    </p>
                    <div className="space-y-2 text-sm text-green-700">
                      <p>✅ Documents reviewed and approved</p>
                      <p>✅ Business practices verified</p>
                      <p>✅ Financial products certified</p>
                      <p>✅ You are now ready to offer Islamic financing products</p>
                    </div>
                  </div>
                )}

                {/* Contact Information Section */}
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <MessageCircle className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-800">Questions or Queries?</h4>
                  </div>
                  <p className="text-sm text-blue-700 mb-4">
                    If you have any questions or need assistance regarding your Shariyah certification, please don't hesitate to contact our team.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Mail className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-blue-600 font-medium">Email</p>
                        <a 
                          href="mailto:shariyah@halalcred.com" 
                          className="text-sm text-blue-800 hover:text-blue-900 hover:underline"
                        >
                          salaam@halalcred.com
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Phone className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-blue-600 font-medium">Phone</p>
                        <a 
                          href="tel:+14253400980" 
                          className="text-sm text-blue-800 hover:text-blue-900 hover:underline"
                        >
                          +1 425 340 0980
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  {uploadedFiles.length > 0 
                    ? `${uploadedFiles.length} certificate(s) issued`
                    : shariyahCompleted 
                      ? 'Certification completed'
                      : 'Awaiting certification from Shariah Advisor'
                  }
                </p>
                <Button
                  onClick={onClose}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ShariyahCertificationModal;

